import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import type { Instance } from "@/types/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CreateInstanceDialog } from "@/components/CreateInstanceDialog";
import { formatDateTime } from "@/lib/utils";

function statusVariant(status: string): "success" | "muted" | "destructive" | "secondary" {
  if (["active", "running", "healthy", "ready"].includes(status)) return "success";
  if (["disabled", "deleting", "canceled"].includes(status)) return "muted";
  if (["failed", "error", "past_due"].includes(status)) return "destructive";
  return "secondary";
}

export function DashboardPage() {
  const { t } = useTranslation("instances");
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = React.useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["instances"],
    queryFn: () => api.get<{ instances: Instance[] }>("/api/admin/instances"),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("dashboard.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("dashboard.description")}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("dashboard.create")}
        </Button>
      </div>

      <CreateInstanceDialog open={createOpen} onOpenChange={setCreateOpen} />

      <Card>
        <CardHeader>
          <CardTitle>{t("dashboard.allTitle")}</CardTitle>
          <CardDescription>{t("dashboard.allDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">
              {t("dashboard.loadFailed", { error: error instanceof Error ? error.message : String(error) })}
            </p>
          ) : !data?.instances?.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t("dashboard.empty")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("dashboard.columns.instance")}</TableHead>
                  <TableHead>{t("dashboard.columns.status")}</TableHead>
                  <TableHead>{t("dashboard.columns.owner")}</TableHead>
                  <TableHead>{t("dashboard.columns.created")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.instances.map((inst) => (
                  <TableRow
                    key={inst.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/instances/${inst.name}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/instances/${inst.name}`);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    aria-label={t("dashboard.openSettings", { name: inst.name })}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{inst.name}</span>
                        {inst.internal && (
                          <Badge className="border-transparent bg-amber-500 text-white hover:bg-amber-500">
                            {t("dashboard.internal")}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{inst.domain}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(inst.status)}>{inst.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {inst.ownerEmail ?? "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(inst.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
